import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { AuthService } from '../../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { DefaultSocketService } from '../../common/websocket';
import { LoggerService } from '../../logger';
import { CaMeetingRequestContextParams } from '../coach-assist';

import { HubMethods } from './model/hub-methods.model';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class SocketService extends DefaultSocketService implements OnDestroy {
    readonly message: Observable<{ hubMethod: string; payload: any }>;
    private readonly _messageSubj: Subject<{ hubMethod: string; payload: any }>;

    constructor(
        private readonly _authService2: AuthService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _logger: LoggerService,
    ) {
        super(_authService2);

        this._messageSubj = new Subject();
        this.message = this._messageSubj.asObservable();

        this._setupBindings();
    }

    ngOnDestroy(): void {
        this._destroy();
    }

    async retrieveCaMeetingRequestContext(
        params: CaMeetingRequestContextParams,
    ): Promise<void> {
        await this.send(HubMethods.RetrieveCaMeetingRequestContext, params);
    }

    async createCaResultForPrep(guideConfigId: string): Promise<void> {
        await this.send(HubMethods.CreateCaResultForPrep, guideConfigId);
    }

    async retrieveCaResult(): Promise<void> {
        await this.send(HubMethods.RetrieveCaResult);
    }

    protected _websocketUrl(): string {
        return `${this._appConfig.API_ENDPOINTS.COACHING ?? ''}/ws`;
    }

    private _setupBindings(): void {
        if (this.created) {
            this._bind();
        }
        this.didCreate
            .pipe(
                tap(() => {
                    this._bind();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        if (this.connected) {
            void this.retrieveCaResult();
        }
        this.didConnect
            .pipe(
                tap(() => {
                    void this.retrieveCaResult();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        const methods: HubMethods[] = [
            HubMethods.OnCaMeetingRequestContext,
            HubMethods.OnCaResultForPrep,
            HubMethods.OnCaResult,
        ];

        methods.forEach((method) => {
            this.on(method, (payload) => {
                this._logger.debug({ method, payload });
                this._messageSubj.next({
                    hubMethod: method,
                    payload,
                });
            });
        });
    }
}
